import React, { createContext, useContext, useMemo, useCallback, useState, useEffect } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import axios from "axios";
import { envEndPoints } from "./config";

const API_BASE_ENV = process.env.REACT_APP_API_BASE_URL;
const API_BASE = API_BASE_ENV && API_BASE_ENV.trim().length ? API_BASE_ENV : (envEndPoints()?.baseUrl || "");
const TOKEN_KEY = "AUTH_TOKEN";
const SIGNED_OUT_KEY = "EXPLICIT_SIGNOUT";

// If the token is missing or not in the form off a string, return True

function isJwtExpired(jwt) {
  if (!jwt || typeof jwt !== "string") return true;
  const parts = jwt.split(".");
  if (parts.length !== 3) return true;
  // Split on the dots, because there are three separate parts to the JWT. If you dont get exactly three parts, the token is invalid.
  try {
    const payload = JSON.parse(atob(parts[1]));
    if (!payload.exp) return true;
    return Date.now() >= payload.exp * 1000;
    // Date.now() is the current time in milliseconds. If the expiration time is in the past, the token is expired.
  } catch {
    return true;
  }
}

// Installs guards that catch 401 (expired/missing token) and 422 (malformed
// token) on protected API responses. Runs at module load time so the fetch
// wrapper and axios interceptor are active before any component renders.
// Without this, the very first API call after a navigation can leave the
// pipeline before React has installed the catcher.
(function installAuthFailureGuards() {
  if (typeof window === "undefined") return;
  if (window.__AUTH_GUARDS_INSTALLED__) return;
  window.__AUTH_GUARDS_INSTALLED__ = true;

  const isProtectedApi = (url) =>
    typeof url === "string" && url.includes("/api/") && !url.includes("/api/login");

  const isAuthFailure = (status) => status === 401 || status === 422;

  let alreadyHandled = false;
  const handleAuthFailure = () => {
    if (alreadyHandled) return;
    alreadyHandled = true;
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.setItem(SIGNED_OUT_KEY, "1");
    } catch { }
    window.location.href = "/";
  };

  const originalFetch = window.fetch;
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    if (isAuthFailure(response.status)) {
      const url = typeof args[0] === "string" ? args[0] : (args[0] && args[0].url) || "";
      if (isProtectedApi(url)) {
        handleAuthFailure();
        // Hang the promise so the caller never sees the error response.
        return new Promise(() => { });
      }
    }
    return response;
  };

  axios.interceptors.response.use(
    (r) => r,
    (error) => {
      if (isAuthFailure(error?.response?.status)) {
        const url = error.config?.url || "";
        if (isProtectedApi(url)) {
          handleAuthFailure();
          return new Promise(() => { });
        }
      }
      return Promise.reject(error);
    }
  );
})();

const AuthContext = createContext({
  isAuthenticated: false,
  token: null,
  user: { token: null },   // always an object
  auth: { token: null },   // legacy alias, always an object
  login: async (_e, _p) => { },
  loginMicrosoft: async () => { },
  logout: async () => { },
  getAuthHeader: () => ({}),
});

export function AuthProvider({ children }) {
  const { instance, accounts } = useMsal();
  const msalAuthed = useIsAuthenticated();

  // keep token and a minimal user object in state, ignoring any expired JWT in storage
  const [token, setToken] = useState(() => {
    try {
      if (localStorage.getItem(SIGNED_OUT_KEY) === "1") return null;
      const stored = localStorage.getItem(TOKEN_KEY);
      if (stored && !isJwtExpired(stored)) return stored;
      // Stored token is expired (or storage held something invalid).
      // Treat that as a forced sign-out so the user lands on the login page
      // and has to sign in again instead of being silently re-authenticated.
      if (stored) {
        localStorage.removeItem(TOKEN_KEY);
        localStorage.setItem(SIGNED_OUT_KEY, "1");
      }
      return null;
    } catch { return null; }
  });
  const [user, setUser] = useState(() => {
    let t = null;
    try {
      if (localStorage.getItem(SIGNED_OUT_KEY) !== "1") {
        const stored = localStorage.getItem(TOKEN_KEY);
        if (stored && !isJwtExpired(stored)) t = stored;
      }
    } catch { }
    return { token: t || null };
  });

  const isAuthenticated = !!token || msalAuthed;

  // EMAIL/PASSWORD: always hit API, never bypass
  const login = useCallback(async (email, password) => {
    try { localStorage.removeItem(SIGNED_OUT_KEY); } catch { }
    const res = await fetch(`${API_BASE}/login/`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ email, password }),
      credentials: "include",
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data?.error || `Login failed (${res.status})`);

    const jwt = data?.access_token;
    if (!jwt) throw new Error("Server did not return a token");

    try { localStorage.setItem(TOKEN_KEY, jwt); } catch { }
    setToken(jwt);

    // make sure Layouts reading user.token don’t crash
    setUser(prev => {
      const base = (data?.user && typeof data.user === "object") ? data.user : { email };
      return { ...base, token: jwt };
    });

    return data;
  }, []);

  const loginMicrosoft = useCallback(async () => {
    try { localStorage.removeItem(SIGNED_OUT_KEY); } catch { }
    await instance.loginRedirect({ scopes: ["User.Read"], prompt: "select_account" });
  }, [instance]);

  const logout = useCallback(async () => {
    try {
      localStorage.removeItem(TOKEN_KEY);
      localStorage.setItem(SIGNED_OUT_KEY, "1");
    } catch { }
    setToken(null);
    setUser({ token: null });
    if (msalAuthed) {
      try {
        await instance.logoutRedirect({ postLogoutRedirectUri: window.location.origin });
      } catch {
        window.location.href = "/";
      }
    } else {
      window.location.href = "/";
    }
  }, [instance, msalAuthed]);

  const getAuthHeader = useCallback(() => (
    token ? { Authorization: `Bearer ${token}` } : {}
  ), [token]);

  // When MSAL signs in: keep minimal user info, AND exchange the Microsoft ID token
  // for our app's JWT so protected API calls (jwt_required) work.
  // Also re-runs whenever the stored JWT is missing or expired so SSO auto-login
  // refreshes the app token in lock step with the AD session.
  useEffect(() => {
    if (!msalAuthed || !accounts?.[0]) return;
    // If you're not signed into Microsoft, stop here.
    if (token && !isJwtExpired(token)) return;
    // If you have a token AND it's still valid, nothing to do.
    try {
      if (localStorage.getItem(SIGNED_OUT_KEY) === "1") return;
    } catch { }
    // If the user explicitly signed out, don't auto-revive the session.
    try { localStorage.removeItem(TOKEN_KEY); } catch { }
    // Token is missing or expired, clear it out and run the exchange below.
    const acct = accounts[0];
    const { username, name } = acct;
    setUser((u) => ({
      ...(u || {}),
      email: u?.email || username,
      name: u?.name || name,
      token: u?.token || null,
    }));

    let cancelled = false;
    (async () => {
      try {
        const result = await instance.acquireTokenSilent({
          scopes: ["openid", "profile", "email"],
          account: acct,
        });
        const idToken = result?.idToken;
        if (!idToken) return;

        const res = await fetch(`${API_BASE}/login/msal`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ id_token: idToken }),
        });
        if (!res.ok) {
          console.error("MSAL → JWT exchange failed:", res.status, await res.text().catch(() => ""));
          return;
        }
        const data = await res.json().catch(() => ({}));
        const jwt = data?.access_token;
        if (!jwt || cancelled) return;

        try { localStorage.setItem(TOKEN_KEY, jwt); } catch { }
        setToken(jwt);
        setUser((prev) => ({ ...(prev || {}), token: jwt }));
      } catch (err) {
        console.error("MSAL → JWT exchange error:", err);
        // Silent acquire failed - send user back to Microsoft for interactive sign-in.
        if (!cancelled) {
          try {
            await instance.loginRedirect({ scopes: ["openid", "profile", "email"] });
          } catch (redirectErr) {
            console.error("loginRedirect after silent failure errored:", redirectErr);
          }
        }
      }
    })();

    return () => { cancelled = true; };
  }, [msalAuthed, accounts, token, instance]);

  // legacy alias to satisfy code that uses auth.token
  const auth = useMemo(() => ({ token: token || null }), [token]);

  const value = useMemo(() => ({
    isAuthenticated,
    token,
    user,
    auth,
    login,
    loginMicrosoft,
    logout,
    getAuthHeader,
  }), [isAuthenticated, token, user, auth, login, loginMicrosoft, logout, getAuthHeader]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export const useAuth = () => useContext(AuthContext);
